import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router";
import { motion } from "framer-motion";
import Popover from "@mui/material/Popover";
import IRMenu from "./IRMenu";
import AuthorizedCapitalModal from "./AuthorizedCapitalModal";
import IssuedCapitalPage from "./IssuedCapitalModal";
import commoncss from "../../../myfin_v2/css/common.module.css";
import buttoncss from "../../../myfin_v2/css/button.module.css";
import { ReactComponent as SearchIcon } from "../../../myfin_v2/assets/icons/searchPri.svg";
import { ReactComponent as FilterIcon } from "../../assets/icons/filter.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close_grey.svg";
import investmentRegisterCss from "../../../myfin_v2/css/investmentRegister.module.css";
import { Button } from "../../../myfin_v2/elements/Button";
import { ReactComponent as PlusPrimaryIcon } from "../../../myfin_v2/assets/icons/plusprimary.svg";
import { ReactComponent as MenuDotsIcon } from "../../../myfin_v2/assets/icons/menu_dot.svg";
import { ReactComponent as EditIcon } from "../../../myfin_v2/assets/icons/edit.svg";
import { ReactComponent as DownGreyIcon } from "../../../myfin_v2/assets/icons/down_grey.svg";
import {
    getShareCapital, getLinkedCompanies, deleteAuthorizedCapital, deleteIssuedCapital
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import swal from "sweetalert";

const ShareCapital = () => {

    const { irId } = useParams();
    const history = useHistory();

    const classOfShareOptions = ["Equity", "Preference", "Lease Leability"];
    const relationshipOptions = ["Director", "Investor", "Promoter", "Employee", "Affiliate"];

    const [anchorEl, setAnchorEl]                                             = useState(null);
    const [openAuthorizedCapitalPopoverId, setOpenAuthorizedCapitalPopoverId] = useState(null);
    const [selectedAuthorizedCapital, setSelectedAuthorizedCapital]           = useState(null);
    const [authorizedModalOpen, setAuthorizedModalOpen]                       = useState(false);
    const [authorizedModalType, setAuthorizedModalType]                       = useState(null);
    const [issuedCapitalAnchorEl, setIssuedCapitalAnchorEl]                   = useState(null);
    const [openIssuedCapitalPopoverId, setOpenIssuedCapitalPopoverId]         = useState(null);
    const [selectedIssuedCapital, setSelectedIssuedCapital]                   = useState(null);

    // ← NEW: full-screen page state instead of modal
    const [showIssuedCapitalPage, setShowIssuedCapitalPage]                   = useState(false);
    const [issuedCapitalPageType, setIssuedCapitalPageType]                   = useState(null);

    const [isEditing, setIsEditing]                                           = useState(false);
    const [isIssuedEditing, setIsIssuedEditing]                               = useState(false);
    const [authorizedCapital, setAuthorizedCapital]                           = useState([]);
    const [issuedCapital, setIssuedCapital]                                   = useState([]);
    const [editableRows, setEditableRows]                                     = useState([]);
    const [editableIssuedRows, setEditableIssuedRows]                         = useState([]);
    const [showSearch, setShowSearch]                                         = useState(false);
    const [companySearch, setCompanySearch]                                   = useState("");
    const [selectedCompany, setSelectedCompany]                               = useState(null);
    const [showHeaderSearch, setShowHeaderSearch]                             = useState(false);
    const [headerSearchValue, setHeaderSearchValue]                           = useState("");
    const [companyList, setCompanyList]                                       = useState([]);
    const [linkedCompanies, setLinkedCompanies]                               = useState([]);

    useEffect(() => {
        if (!irId) return;
        getLinkedCompanies(irId)
            .then((res) => {
                const companies = res.data?.companies || [];
                setCompanyList(companies);
                setLinkedCompanies(companies);
            })
            .catch((err) => console.error("Failed to fetch linked companies:", err));
    }, [irId]);

    const filteredCompanies = companyList.filter((c) =>
        c.company_name.toLowerCase().includes(companySearch.toLowerCase())
    );

    const fetchShareCapitalData = async () => {
        try {
            const res = await getShareCapital(irId, selectedCompany._id);
            if (res.data.success) {
                const authCap   = res.data.sharedCapital.authorizedCapital || [];
                const issuedCap = res.data.sharedCapital.issuedCapital     || [];
                setAuthorizedCapital(authCap);
                setIssuedCapital(issuedCap);
                setEditableRows(authCap);
                setEditableIssuedRows(issuedCap);
            }
        } catch (error) {
            console.log("Error fetching Share Capital:", error);
        }
    };

    useEffect(() => {
        if (filteredCompanies.length > 0 && !selectedCompany) {
            setSelectedCompany(filteredCompanies[0]);
        }
    }, [filteredCompanies]);

    useEffect(() => {
        if (selectedCompany) fetchShareCapitalData();
    }, [selectedCompany?._id]);

    useEffect(() => {
        const closeSearch = (e) => {
            if (!e.target.closest(`.${investmentRegisterCss.companyPanelHeader}`)) {
                setShowSearch(false);
            }
        };
        document.addEventListener("click", closeSearch);
        return () => document.removeEventListener("click", closeSearch);
    }, []);

    // ── Authorized Capital handlers ───────────────────────────────────────────
    const handleMenuClick  = (event, id) => { setAnchorEl(event.currentTarget); setOpenAuthorizedCapitalPopoverId(id); };
    const handleClose      = ()          => { setAnchorEl(null); setOpenAuthorizedCapitalPopoverId(null); };
    const handleEdit       = (item)      => { setSelectedAuthorizedCapital(item); setAuthorizedModalType("edit"); setAuthorizedModalOpen(true); handleClose(); };
    const handleModalClose = ()          => { setAuthorizedModalOpen(false); setSelectedAuthorizedCapital(null); setAuthorizedModalType(null); };
    const handleAdd        = ()          => { setSelectedAuthorizedCapital(null); setAuthorizedModalType("add"); setAuthorizedModalOpen(true); };

    const handleDelete = async (id) => {
        const willDelete = await swal({
            title: "Are you sure?",
            text: "Authorized capital record will be deleted!",
            buttons: ["Cancel", "Delete"],
            dangerMode: true,
        });
        if (!willDelete) return;
        try {
            const res = await deleteAuthorizedCapital(irId, id);
            if (res.status === 200) { swal("Deleted!", "Authorized capital removed.", "success"); fetchShareCapitalData(); }
        } catch (err) { console.log(err); swal("Error", "Delete failed", "error"); }
        handleClose();
    };

    const handleAuthorizedChange = (id, field, value) =>
        setEditableRows((prev) => prev.map((row) => row._id === id ? { ...row, [field]: value } : row));

    // ── Issued Capital handlers ───────────────────────────────────────────────
    const handleIssuedCapitalPopoverClick = (event, id) => { setIssuedCapitalAnchorEl(event.currentTarget); setOpenIssuedCapitalPopoverId(id); };
    const handleIssuedCapitalPopoverClose = ()          => { setIssuedCapitalAnchorEl(null); setOpenIssuedCapitalPopoverId(null); };

    // ← CHANGED: open full-screen page instead of modal
    const handleIssuedCapitalEdit = (item) => {
        setSelectedIssuedCapital(item);
        setIssuedCapitalPageType("edit");
        setShowIssuedCapitalPage(true);
        handleIssuedCapitalPopoverClose();
    };

    const handleAddIssuedCapital = () => {
        setSelectedIssuedCapital(null);
        setIssuedCapitalPageType("add");
        setShowIssuedCapitalPage(true);
    };

    // ← CHANGED: close full-screen page
    const handleIssuedCapitalPageClose = () => {
        setShowIssuedCapitalPage(false);
        setSelectedIssuedCapital(null);
        setIssuedCapitalPageType(null);
    };

    const handleIssuedCapitalDelete = async (id) => {
        const willDelete = await swal({
            title: "Are you sure?",
            text: "Issued capital record will be deleted!",
            buttons: ["Cancel", "Delete"],
            dangerMode: true,
        });
        if (!willDelete) return;
        try {
            const res = await deleteIssuedCapital(irId, id);
            if (res.status === 200) { swal("Deleted!", "Issued capital removed.", "success"); fetchShareCapitalData(); }
        } catch (err) { console.log(err); swal("Error", "Delete failed", "error"); }
        handleIssuedCapitalPopoverClose();
    };

    const handleIssuedChange = (id, field, value) =>
        setEditableIssuedRows((prev) => prev.map((row) => row._id === id ? { ...row, [field]: value } : row));

    const open = Boolean(anchorEl);

    const displayAuthorizedRows = editableRows.filter((item) => {
        if (!headerSearchValue) return true;
        const search = headerSearchValue.toLowerCase();
        return (
            (item.classOfShare && item.classOfShare.toLowerCase().includes(search)) ||
            (item.noOfShares && item.noOfShares.toString().includes(search)) ||
            (item.faceValue && item.faceValue.toString().includes(search)) ||
            (item.amount && item.amount.toString().includes(search))
        );
    });

    const displayIssuedRows = editableIssuedRows.filter((item) => {
        if (!headerSearchValue) return true;
        const search = headerSearchValue.toLowerCase();
        return (
            (item.shareHolderName && item.shareHolderName.toLowerCase().includes(search)) ||
            (item.relationship && item.relationship.toLowerCase().includes(search)) ||
            (item.classOfShare && item.classOfShare.toLowerCase().includes(search)) ||
            (item.noOfShares && item.noOfShares.toString().includes(search)) ||
            (item.faceValue && item.faceValue.toString().includes(search)) ||
            (item.amount && item.amount.toString().includes(search)) ||
            (item.holdingPercentage && item.holdingPercentage.toString().includes(search))
        );
    });

    // Removed early return for IssuedCapitalPage to render it as a modal overlay instead

    return (
        <>
            <div className={`d-flex ${commoncss.pageContent}`}>
                <IRMenu activeTab={"Share Capital"} registerID={irId} />
                <motion.div
                    initial={{ x: 200, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 200, opacity: 0 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                    className={investmentRegisterCss.irLayout}
                >
                    {/* ── Header ── */}
                    <div className={investmentRegisterCss.iRHeader}>
                        {showHeaderSearch ? (
                            <div className={investmentRegisterCss.headerSearchBar}>
                                <SearchIcon className={investmentRegisterCss.headerSearchBarIcon} />
                                <input autoFocus type="text" className={investmentRegisterCss.headerSearchInput} placeholder="Type something..." value={headerSearchValue} onChange={(e) => setHeaderSearchValue(e.target.value)} />
                                <CloseIcon className={investmentRegisterCss.headerSearchClose} onClick={() => { setShowHeaderSearch(false); setHeaderSearchValue(""); }} />
                            </div>
                        ) : (
                            <SearchIcon style={{ cursor: "pointer" }} onClick={() => setShowHeaderSearch(true)} />
                        )}
                        <FilterIcon />
                    </div>

                    <div className={investmentRegisterCss.shareCapitalLayout}>

                        {/* ── Company Panel ── */}
                        <div className={investmentRegisterCss.companyPanel}>
                            <div className={investmentRegisterCss.companyPanelHeader}>
                                <span className={investmentRegisterCss.companyPanelTitle}>Select Company</span>
                                <span className={investmentRegisterCss.companyPanelSearchIcon}>
                                    <SearchIcon onClick={() => setShowSearch(prev => !prev)} />
                                </span>
                            </div>
                            {showSearch && (
                                <div className={investmentRegisterCss.companySearchInputWrapper}>
                                    <input type="text" className={investmentRegisterCss.companySearchInput} placeholder="Search company..." value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} autoFocus />
                                </div>
                            )}
                            <ul className={investmentRegisterCss.companyList}>
                                {filteredCompanies.map((company) => (
                                    <li
                                        key={company._id}
                                        className={`${investmentRegisterCss.companyListItem} ${selectedCompany?._id === company._id ? investmentRegisterCss.companyListItemActive : ""}`}
                                        onClick={() => setSelectedCompany(company)}
                                        data-tooltip={company.company_name}
                                        title={company.company_name}
                                    >
                                        {company.company_name}
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* ── Tables ── */}
                        <div className={investmentRegisterCss.tableLayout}>

                            {/* ── Authorized Capital Table ── */}
                            <div className={investmentRegisterCss.tableConatiner}>
                                <div className={investmentRegisterCss.tableTitle}>
                                    <span>Authorized Capital</span>
                                    {!isEditing ? (
                                        <span className="d-flex gap-1">
                                            <Button btnColor={buttoncss.greyBg} icon={<EditIcon />} class_={investmentRegisterCss.editBtn} onClick={() => setIsEditing(true)} />
                                            <Button btnColor={buttoncss.greyBg} icon={<PlusPrimaryIcon />} class_={investmentRegisterCss.addBtnRow} onClick={handleAdd} />
                                        </span>
                                    ) : (
                                        <div className={investmentRegisterCss.saveCancelWrap}>
                                            <button className={investmentRegisterCss.cancelBtn} onClick={() => { setIsEditing(false); setEditableRows([...authorizedCapital]); }}>Cancel</button>
                                            <button className={investmentRegisterCss.saveBtn} onClick={() => { console.log("Saved:", editableRows); setIsEditing(false); }}>Save</button>
                                        </div>
                                    )}
                                </div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: "left", paddingLeft: "20px" }}>Class of share</th>
                                            <th style={{ textAlign: "right" }}>Number of shares</th>
                                            <th style={{ textAlign: "right" }}>Face value</th>
                                            <th style={{ textAlign: "right" }}>Amount</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayAuthorizedRows.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "#b3c0bd" }}>No authorized capital data</td>
                                            </tr>
                                        ) : (
                                            displayAuthorizedRows.map((item) => (
                                                <tr key={item._id}>
                                                    <td style={{ textAlign: "left", paddingLeft: "20px" }}>
                                                        {isEditing ? (
                                                            <div className={investmentRegisterCss.dropdownWrapper}>
                                                                <select className={investmentRegisterCss.dropdownInput} value={item.classOfShare} onChange={(e) => handleAuthorizedChange(item._id, "classOfShare", e.target.value)}>
                                                                    <option value="" disabled>Select class of share</option>
                                                                    {classOfShareOptions.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                                                </select>
                                                                <span className={investmentRegisterCss.dropdownArrow}><DownGreyIcon /></span>
                                                            </div>
                                                        ) : (item.classOfShare)}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {isEditing ? (<input type="number" value={item.noOfShares} className={investmentRegisterCss.inputShare} onChange={(e) => handleAuthorizedChange(item._id, "noOfShares", e.target.value)} placeholder="Enter Number..." />) : (item.noOfShares?.toLocaleString())}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {isEditing ? (<input type="number" className={investmentRegisterCss.inputShare} value={item.faceValue} onChange={(e) => handleAuthorizedChange(item._id, "faceValue", e.target.value)} placeholder="Enter Value..." />) : (item.faceValue)}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {isEditing ? (<input type="number" className={investmentRegisterCss.inputShare} value={item.amount} onChange={(e) => handleAuthorizedChange(item._id, "amount", e.target.value)} placeholder="Enter Amount..." />) : (item.amount?.toLocaleString())}
                                                    </td>
                                                    <td>
                                                        <MenuDotsIcon style={{ cursor: "pointer", marginRight: 20 }} onClick={(e) => handleMenuClick(e, item._id)} />
                                                        <Popover open={open && openAuthorizedCapitalPopoverId === item._id} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                                                            <div className={investmentRegisterCss.irOptions}>
                                                                <span style={{ borderBottom: "1px solid #EAEEED" }} onClick={() => handleEdit(item)}>Edit</span>
                                                                <span onClick={() => handleDelete(item._id)}>Delete</span>
                                                            </div>
                                                        </Popover>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* ── Issued Capital Table ── */}
                            <div className={investmentRegisterCss.tableConatiner}>
                                <div className={investmentRegisterCss.tableTitle}>
                                    <span>Issued Capital</span>
                                    {!isIssuedEditing ? (
                                        <span className="d-flex gap-1">
                                            <Button btnColor={buttoncss.greyBg} icon={<EditIcon />} class_={investmentRegisterCss.editBtn} onClick={() => setIsIssuedEditing(true)} />
                                            <Button btnColor={buttoncss.greyBg} icon={<PlusPrimaryIcon />} class_={investmentRegisterCss.addBtnRow} onClick={handleAddIssuedCapital} />
                                        </span>
                                    ) : (
                                        <div className={investmentRegisterCss.saveCancelWrap}>
                                            <button className={investmentRegisterCss.cancelBtn} onClick={() => { setIsIssuedEditing(false); setEditableIssuedRows([...issuedCapital]); }}>Cancel</button>
                                            <button className={investmentRegisterCss.saveBtn} onClick={() => { console.log("Issued Saved:", editableIssuedRows); setIsIssuedEditing(false); }}>Save</button>
                                        </div>
                                    )}
                                </div>
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: "left", paddingLeft: "20px" }}>Share holder</th>
                                            <th>Relationship</th>
                                            <th>Class of Share</th>
                                            <th style={{ textAlign: "right" }}>No of shares</th>
                                            <th style={{ textAlign: "right" }}>Face value</th>
                                            <th style={{ textAlign: "right" }}>Amount</th>
                                            <th style={{ textAlign: "right" }}>Holding %</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayIssuedRows.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" style={{ textAlign: "center", padding: "20px", color: "#b3c0bd" }}>No issued capital data</td>
                                            </tr>
                                        ) : (
                                            displayIssuedRows.map((item) => (
                                                <tr key={item._id}>
                                                    <td style={{ textAlign: "left", paddingLeft: "20px" }}>{item.shareHolderName}</td>
                                                    <td>
                                                        {isIssuedEditing ? (
                                                            <div className={investmentRegisterCss.dropdownWrapper}>
                                                                <select className={investmentRegisterCss.dropdownInput} value={item.relationship} onChange={(e) => handleIssuedChange(item._id, "relationship", e.target.value)}>
                                                                    {relationshipOptions.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                                                </select>
                                                                <span className={investmentRegisterCss.dropdownArrow}><DownGreyIcon /></span>
                                                            </div>
                                                        ) : (item.relationship)}
                                                    </td>
                                                    <td>
                                                        {isIssuedEditing ? (
                                                            <div className={investmentRegisterCss.dropdownWrapper}>
                                                                <select className={investmentRegisterCss.dropdownInput} value={item.classOfShare} onChange={(e) => handleIssuedChange(item._id, "classOfShare", e.target.value)}>
                                                                    {classOfShareOptions.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                                                                </select>
                                                                <span className={investmentRegisterCss.dropdownArrow}><DownGreyIcon /></span>
                                                            </div>
                                                        ) : (item.classOfShare)}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {isIssuedEditing ? (<input type="number" className={investmentRegisterCss.inputShare} value={item.noOfShares} placeholder="Enter Number..." onChange={(e) => handleIssuedChange(item._id, "noOfShares", e.target.value)} />) : (item.noOfShares?.toLocaleString())}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {isIssuedEditing ? (<input type="number" className={investmentRegisterCss.inputShare} value={item.faceValue} placeholder="Enter Values..." onChange={(e) => handleIssuedChange(item._id, "faceValue", e.target.value)} />) : (item.faceValue)}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {isIssuedEditing ? (<input type="number" className={investmentRegisterCss.inputShare} value={item.amount} placeholder="Enter Amount..." onChange={(e) => handleIssuedChange(item._id, "amount", e.target.value)} />) : (item.amount?.toLocaleString())}
                                                    </td>
                                                    <td style={{ textAlign: "right" }}>
                                                        {isIssuedEditing ? (<input type="number" className={investmentRegisterCss.inputShare} value={item.holdingPercentage} placeholder="Enter Holding %..." onChange={(e) => handleIssuedChange(item._id, "holdingPercentage", e.target.value)} />) : (`${item.holdingPercentage}%`)}
                                                    </td>
                                                    <td>
                                                        <MenuDotsIcon style={{ cursor: "pointer" }} onClick={(e) => handleIssuedCapitalPopoverClick(e, item._id)} />
                                                        <Popover open={openIssuedCapitalPopoverId === item._id} anchorEl={issuedCapitalAnchorEl} onClose={handleIssuedCapitalPopoverClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                                                            <div className={investmentRegisterCss.irOptions}>
                                                                <span style={{ borderBottom: "1px solid #EAEEED" }} onClick={() => handleIssuedCapitalEdit(item)}>Edit</span>
                                                                <span onClick={() => handleIssuedCapitalDelete(item._id)}>Delete</span>
                                                            </div>
                                                        </Popover>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Authorized Capital Modal stays as modal — no change */}
            {authorizedModalOpen && (
                <AuthorizedCapitalModal
                    companyId={selectedCompany._id}
                    handleClose={handleModalClose}
                    data={selectedAuthorizedCapital}
                    type={authorizedModalType}
                    classOfShareOptions={classOfShareOptions}
                    investmentID={irId}
                    refreshData={fetchShareCapitalData}
                />
            )}
            
            {showIssuedCapitalPage && (
                <IssuedCapitalPage
                    onBack={handleIssuedCapitalPageClose}
                    data={selectedIssuedCapital}
                    type={issuedCapitalPageType}
                    classOfShareOptions={classOfShareOptions}
                    relationshipOptions={relationshipOptions}
                    investmentID={irId}
                    refreshData={fetchShareCapitalData}
                    linkedCompanies={linkedCompanies}
                    companyId={selectedCompany?._id}
                    companyName={selectedCompany?.company_name}
                />
            )}
        </>
    );
};

export default ShareCapital;